import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Flashcard } from '../types';
import { Button } from './ui/Button';
import { downloadStandaloneGame } from '../utils/matchGameExporter';
import {
  Clock,
  RotateCcw,
  CheckCircle,
  XCircle,
  Shuffle,
  Target,
  Link2,
  Trophy,
  Crown,
  History,
  Download,
} from 'lucide-react';

interface MatchGameProps {
  cards: Flashcard[];
  deckId?: string;
  deckName?: string;
  onComplete?: (stats: { moves: number; time: number; mistakes: number }) => void;
  onExit?: () => void;
}

interface TermItem {
  id: string;
  content: string;
  pairId: string;
  isMatched: boolean;
}

interface DefinitionItem {
  id: string;
  content: string;
  pairId: string;
  isMatched: boolean;
}

interface MatchResult {
  id: string;
  deckId?: string;
  deckName?: string;
  date: string;
  moves: number;
  mistakes: number;
  time: number;
  accuracy: number;
  totalPairs: number;
}

const STORAGE_KEY = 'flashcard-match-results';
const MAX_HISTORY = 20;

const loadResults = (): MatchResult[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const saveResult = (result: MatchResult) => {
  const results = loadResults();
  results.unshift(result);
  if (results.length > MAX_HISTORY) {
    results.pop();
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(results));
};

const getBestResult = (deckId?: string): MatchResult | null => {
  const results = loadResults();
  const filtered = deckId ? results.filter((r) => r.deckId === deckId) : results;
  if (filtered.length === 0) return null;
  return filtered.reduce((best, r) => (r.accuracy > best.accuracy ? r : best));
};

export const MatchGame: React.FC<MatchGameProps> = ({
  cards,
  deckId,
  deckName,
  onComplete,
  onExit,
}) => {
  const navigate = useNavigate();
  const [terms, setTerms] = useState<TermItem[]>([]);
  const [definitions, setDefinitions] = useState<DefinitionItem[]>([]);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [moves, setMoves] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [wrongMatch, setWrongMatch] = useState<{ termId: string; defId: string } | null>(null);
  const [correctMatch, setCorrectMatch] = useState<{ termId: string; defId: string } | null>(null);
  const [bestResult, setBestResult] = useState<MatchResult | null>(null);
  const [history, setHistory] = useState<MatchResult[]>([]);

  const initializeGame = useCallback(() => {
    if (cards.length < 2) return;

    setBestResult(getBestResult(deckId));
    setHistory(deckId ? loadResults().filter((r) => r.deckId === deckId).slice(0, 5) : loadResults().slice(0, 5));

    const cardPairs = cards.slice(0, Math.min(cards.length, 12));

    const newTerms: TermItem[] = cardPairs.map((card, index) => ({
      id: `term-${index}`,
      content: card.front,
      pairId: `pair-${index}`,
      isMatched: false,
    }));

    const shuffledDefs: DefinitionItem[] = cardPairs
      .map((card, index) => ({
        id: `def-${index}`,
        content: card.back,
        pairId: `pair-${index}`,
        isMatched: false,
      }))
      .sort(() => Math.random() - 0.5);

    setTerms(newTerms);
    setDefinitions(shuffledDefs);
    setSelectedTermId(null);
    setMoves(0);
    setMistakes(0);
    setTimeElapsed(0);
    setIsPlaying(true);
    setWrongMatch(null);
    setCorrectMatch(null);
  }, [cards, deckId]);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const handleDefClick = useCallback((defId: string) => {
    if (!isPlaying || !selectedTermId) return;

    const def = definitions.find((d) => d.id === defId);
    if (!def || def.isMatched) return;

    const term = terms.find((t) => t.id === selectedTermId);
    if (!term) return;

    const newMoves = moves + 1;

    if (term.pairId === def.pairId) {
      setCorrectMatch({ termId: selectedTermId, defId });

      const updatedTerms = terms.map((t) => (t.id === selectedTermId ? { ...t, isMatched: true } : t));
      const updatedDefs = definitions.map((d) => (d.id === defId ? { ...d, isMatched: true } : d));

      setTimeout(() => {
        setTerms(updatedTerms);
        setDefinitions(updatedDefs);
        setCorrectMatch(null);
        setSelectedTermId(null);
        setMoves(newMoves);

        if (updatedTerms.every((t) => t.isMatched)) {
          const accuracy = newMoves > 0 ? Math.round(((newMoves - mistakes) / newMoves) * 100) : 100;
          const result: MatchResult = {
            id: `match-${Date.now()}`,
            deckId,
            deckName,
            date: new Date().toISOString(),
            moves: newMoves,
            mistakes,
            time: timeElapsed,
            accuracy,
            totalPairs: terms.length,
          };
          saveResult(result);
          setIsPlaying(false);
          onComplete?.({ moves: newMoves, time: timeElapsed, mistakes });
          navigate('/accomplishments');
        }
      }, 300);
    } else {
      setWrongMatch({ termId: selectedTermId, defId });
      setMistakes((m) => m + 1);
      setMoves(newMoves);

      setTimeout(() => {
        setWrongMatch(null);
        setSelectedTermId(null);
      }, 600);
    }
  }, [isPlaying, selectedTermId, definitions, terms, moves, mistakes, timeElapsed, deckId, deckName, onComplete, navigate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTermClass = (term: TermItem) => {
    const isSelected = selectedTermId === term.id;
    const isWrong = wrongMatch?.termId === term.id;
    const isCorrect = correctMatch?.termId === term.id;

    if (term.isMatched) return 'bg-green-100 dark:bg-green-900/40 border-2 border-green-400 dark:border-green-600';
    if (isCorrect) return 'bg-green-200 dark:bg-green-800/50 border-2 border-green-500';
    if (isWrong) return 'bg-red-100 dark:bg-red-900/40 border-2 border-red-400 dark:border-red-600';
    if (isSelected) return 'bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-400 dark:border-blue-600';
    return 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500';
  };

  const getDefClass = (def: DefinitionItem) => {
    const isWrong = wrongMatch?.defId === def.id;
    const isCorrect = correctMatch?.defId === def.id;

    if (def.isMatched) return 'bg-green-100 dark:bg-green-900/40 border-2 border-green-400 dark:border-green-600';
    if (isCorrect) return 'bg-green-200 dark:bg-green-800/50 border-2 border-green-500';
    if (isWrong) return 'bg-red-100 dark:bg-red-900/40 border-2 border-red-400 dark:border-red-600';
    if (selectedTermId && !def.isMatched) return 'bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500';
    return 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700';
  };

  const getTermIcon = (term: TermItem) => {
    const isWrong = wrongMatch?.termId === term.id;
    const isCorrect = correctMatch?.termId === term.id;

    if (term.isMatched || isCorrect) return <CheckCircle className="w-5 h-5 text-white" />;
    if (isWrong) return <XCircle className="w-5 h-5 text-white" />;
    if (selectedTermId === term.id) return <span className="text-sm font-bold text-white">★</span>;
    return <span className="text-sm font-bold text-gray-600 dark:text-gray-400">{terms.indexOf(term) + 1}</span>;
  };

  const getTermIconBg = (term: TermItem) => {
    const isWrong = wrongMatch?.termId === term.id;
    const isCorrect = correctMatch?.termId === term.id;

    if (term.isMatched || isCorrect) return 'bg-green-500';
    if (isWrong) return 'bg-red-500';
    if (selectedTermId === term.id) return 'bg-blue-500';
    return 'bg-gray-200 dark:bg-gray-700';
  };

  if (cards.length < 2) {
    return (
      <div className="text-center py-16">
        <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Not Enough Cards
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          You need at least 2 cards to play the matching game.
        </p>
        <Button onClick={onExit}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Match Game</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Match each term to its correct definition
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => downloadStandaloneGame({ cards, deckName })} leftIcon={<Download className="w-4 h-4" />}>
            Export
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} leftIcon={<History className="w-4 h-4" />}>
            {showHistory ? 'Hide' : 'History'}
          </Button>
          <Button variant="ghost" size="sm" onClick={initializeGame} leftIcon={<Shuffle className="w-4 h-4" />}>
            Shuffle
          </Button>
          <Button variant="ghost" size="sm" onClick={onExit}>Exit</Button>
        </div>
      </div>

      {/* Best Result Banner */}
      {bestResult && isPlaying && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-center gap-3">
          <Crown className="w-5 h-5 text-amber-600" />
          <div className="text-sm">
            <span className="text-amber-800 dark:text-amber-300 font-medium">Best: </span>
            <span className="text-amber-700 dark:text-amber-400">{bestResult.accuracy}%</span>
            <span className="text-amber-600 dark:text-amber-500 mx-1">·</span>
            <span className="text-amber-700 dark:text-amber-400">{formatTime(bestResult.time)}</span>
          </div>
        </div>
      )}

      {/* History Panel */}
      {showHistory && history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <History className="w-4 h-4" /> Recent Results
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {history.map((result) => (
              <div key={result.id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <div className="flex items-center gap-3">
                  {result.accuracy === 100 ? (
                    <Trophy className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <div className="w-4 h-4" />
                  )}
                  <span className="text-gray-600 dark:text-gray-400">{result.deckName || 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500 dark:text-gray-400">{formatDate(result.date)}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{result.accuracy}%</span>
                  <span className="text-gray-500 dark:text-gray-400">{formatTime(result.time)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-gray-600 dark:text-gray-300">{formatTime(timeElapsed)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gray-400" />
          <span className="text-gray-600 dark:text-gray-300">{moves} pairs</span>
        </div>
        <div className="flex items-center gap-2">
          {wrongMatch && <XCircle className="w-4 h-4 text-red-500 animate-pulse" />}
          <span className="text-gray-600 dark:text-gray-300">{mistakes} mistakes</span>
        </div>
      </div>

      {/* Matching Grid */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Terms ({terms.filter((t) => t.isMatched).length}/{terms.length})
          </div>
          {terms.map((term) => (
            <button
              key={term.id}
              onClick={() => { if (!term.isMatched && isPlaying) setSelectedTermId(term.id); }}
              disabled={!isPlaying || term.isMatched}
              className={`w-full p-4 rounded-xl text-left transition-all duration-200 ${getTermClass(term)} ${!isPlaying || term.isMatched ? '' : 'cursor-pointer hover:scale-[1.01]'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getTermIconBg(term)}`}>
                  {getTermIcon(term)}
                </div>
                <span className={`font-medium ${term.isMatched ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-gray-100'}`}>
                  {term.content}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Definitions ({definitions.filter((d) => d.isMatched).length}/{definitions.length})
          </div>
          {definitions.map((def) => {
            const isCorrect = correctMatch?.defId === def.id;
            const isWrong = wrongMatch?.defId === def.id;
            return (
              <button
                key={def.id}
                onClick={() => handleDefClick(def.id)}
                disabled={!isPlaying || def.isMatched || !selectedTermId}
                className={`w-full p-4 rounded-xl text-left transition-all duration-200 ${getDefClass(def)} ${!isPlaying || def.isMatched || !selectedTermId ? '' : 'cursor-pointer hover:scale-[1.01]'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    def.isMatched || isCorrect ? 'bg-green-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {def.isMatched || isCorrect ? <CheckCircle className="w-5 h-5" /> : isWrong ? <XCircle className="w-5 h-5" /> : <Link2 className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${def.isMatched ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                    {def.content}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Instructions */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        {wrongMatch ? 'Wrong match! Try again...' : correctMatch ? 'Correct! Well done!' : selectedTermId ? 'Now click the matching definition' : 'Click a term to start matching'}
      </div>
    </div>
  );
};

export default MatchGame;